import Vue from 'vue'
import { S, G, M, A, ReceivedValues } from './types'
import { Getters, Mutations, Actions } from 'vuex'
import BleConnectinManager from '@/ble/connectionManager'
import { BleProviderEventTypes } from '@/ble/constants'
import { isEcgServiceHeartRateValues } from '@/types/utils'
import { BleDeviceTypes } from '@/constants/ble'
import { BleConnectionStatus } from '@/ble/constants'
import { SynchronizedStorage } from '@/synchronizedStorage'
import { LocalStorageKeys } from '@/constants/localStorage'

const valueFilter = (values: Partial<ReceivedValues>) =>
  (Object.keys(values) as Array<keyof ReceivedValues>).reduce(
    (filtered, key) =>
      values[key] && values[key] !== 0
        ? Object.assign(filtered, { [key]: values[key] })
        : filtered,
    {} as Partial<ReceivedValues>
  )

const createInitialReceivedValues = (): ReceivedValues => ({
  systolicPressure: null, // 血圧上
  diastolicPressure: null, // 血圧下
  pulseRate: null, // 脈拍数 (pr)
  heartRate: null, // 心拍数（ECGのprより取得）
  temperature: null, // 体温
  weight: null, // 体重
  spo2: null, // SpO2
  glucose: null // 血糖値
})

/* -------------------------------------------------------------------------- */

const state = (): S => ({
  devices: {},
  unsubscribe: {},
  receivedValues: createInitialReceivedValues(),
  autoSending: false
})

/* -------------------------------------------------------------------------- */

const getters: Getters<S, G> = {
  devices: ({ devices }) => devices,
  unsubscribe: ({ unsubscribe }) => unsubscribe,
  getAddressByDeviceType: ({ devices }) => deviceType =>
    Object.keys(devices).find(
      address => devices[address].deviceType === deviceType
    ) || null,
  receivedValues: ({ receivedValues }) => receivedValues,
  autoSending: ({ autoSending }) => autoSending
}

/* -------------------------------------------------------------------------- */

const mutations: Mutations<S, M> = {
  setDevice: (state, device) => {
    Vue.set(state.devices, device.address, device)
  },

  deleteDevice: (state, { address }) => {
    Vue.delete(state.devices, address)
  },

  setConnected: (state, { address, status }) => {
    Vue.set(state.devices[address], 'connected', status)
    Vue.set(state.devices[address], 'logicalConnected', status)
  },

  setUnsubscribe: (state, { eventType, unsubscribe }) => {
    Vue.set(state.unsubscribe, eventType, unsubscribe)
  },

  deleteUnsubscribe: (state, eventType) => {
    Vue.delete(state.unsubscribe, eventType)
  },

  setReceivedValues: (state, values) => {
    ;(Object.keys(values) as Array<keyof ReceivedValues>).forEach(key => {
      Vue.set(state.receivedValues, key, values[key])
    })
  },

  clearReceivedValues: state => {
    state.receivedValues = createInitialReceivedValues()
  },

  updateAutoSending: (state, sending) => {
    state.autoSending = sending
  }
}

/* -------------------------------------------------------------------------- */

const actions: Actions<S, A, G, M> = {
  async initialize({ commit, rootGetters, dispatch }) {
    if (!BleConnectinManager.isSupport()) {
      return
    }

    BleConnectinManager.init()

    const bleSettings = { ...rootGetters['appSettings/ble'] }

    Object.keys(bleSettings).forEach(address => {
      if (!bleSettings[address].logicalConnected) {
        delete bleSettings[address]
      }
    })

    Object.keys(bleSettings).forEach(address => {
      commit('setDevice', bleSettings[address])
      const { deviceType, deviceName, deviceDisplayName } = bleSettings[address]
      BleConnectinManager.setDevice({
        address,
        deviceType,
        deviceName,
        deviceDisplayName,
        connected: false
      })
    })

    BleConnectinManager.activateAllConnections()

    if (
      Object.keys(bleSettings).length !==
      Object.keys(rootGetters['appSettings/ble']).length
    ) {
      await (dispatch as any)(
        'appSettings/update',
        { ble: { ...bleSettings } },
        { root: true }
      )
    }

    const provider = BleConnectinManager.getProvider()

    const unsubscribe = provider.addListener(
      BleProviderEventTypes.RECEIVE_VALUE,
      readVal => {
        switch (readVal.deviceType) {
          case BleDeviceTypes.ELECTRONIC_BLOOD_PRESSURE_MONITOR:
            commit(
              'setReceivedValues',
              valueFilter({
                systolicPressure: readVal.systolicPressure,
                diastolicPressure: readVal.diastolicPressure,
                pulseRate: readVal.pr
              })
            )
            break
          case BleDeviceTypes.MEDICAL_BLOOD_PRESSURE_MONITOR:
            commit(
              'setReceivedValues',
              valueFilter({
                systolicPressure: readVal.systolicPressure,
                diastolicPressure: readVal.diastolicPressure,
                pulseRate: readVal.pr
              })
            )
            break
          case BleDeviceTypes.ELECTRONIC_THERMOMETER:
            commit('setReceivedValues', {
              temperature: readVal.temperature
            })
            break
          case BleDeviceTypes.BLOOD_SUGAR_LEVEL_MEASUREMENT:
            commit(
              'setReceivedValues',
              valueFilter({
                glucose: readVal.glucose
              })
            )
            break
          case BleDeviceTypes.SPO2_MONITOR_MIGHTY_SAT:
            commit(
              'setReceivedValues',
              valueFilter({
                spo2: readVal.spo2,
                pulseRate: readVal.pr
              })
            )
            break
          case BleDeviceTypes.ECG_SERVICE:
            if (isEcgServiceHeartRateValues(readVal)) {
              commit(
                'setReceivedValues',
                valueFilter({ heartRate: readVal.heartRate })
              )
            }
            break
          case BleDeviceTypes.SPO2_MONITOR_TM1121:
            commit(
              'setReceivedValues',
              valueFilter({
                spo2: readVal.spo2,
                pulseRate: readVal.pr
              })
            )
            break
          case BleDeviceTypes.NONCONTACT_THERMOMETER:
            commit(
              'setReceivedValues',
              valueFilter({
                temperature: readVal.temperature
              })
            )
            break
          case BleDeviceTypes.BODY_COMPOSITION_MONITOR:
            commit(
              'setReceivedValues',
              valueFilter({
                weight: readVal.weight
              })
            )
            break
        }
      }
    )
    commit('setUnsubscribe', {
      eventType: BleProviderEventTypes.RECEIVE_VALUE,
      unsubscribe
    })

    // 1秒おきにコネクションの状態をログに出力する
    const connectionStatusMap = {
      [BleConnectionStatus.LOGICALLY_CONNECTED]: 'LOGICALLY_CONNECTED',
      [BleConnectionStatus.DISCONNECTED]: 'DISCONNECTED',
      [BleConnectionStatus.CONNECTED]: 'CONNECTED',
      [BleConnectionStatus.PENDING]: 'PENDING'
    }
    setInterval(() => {
      const connections = BleConnectinManager.connections
      Object.keys(connections).forEach(adderss => {
        console.log(
          '[INTERVAL_LOG] deviceType: ' +
            connections[adderss].deviceType +
            ' adderss: ' +
            connections[adderss].address +
            ' status: ' +
            connectionStatusMap[connections[adderss].connectionStatus]
        )
      })
    }, 10000)
  },

  async destroy({ getters, commit }) {
    if (!BleConnectinManager.isSupport()) {
      return
    }

    Object.keys(getters['unsubscribe']).forEach(eventType => {
      getters['unsubscribe'][Number(eventType)]()
      commit('deleteUnsubscribe', Number(eventType))
    })
    Object.keys(getters['devices']).forEach(address => {
      commit('deleteDevice', { address })
    })

    await BleConnectinManager.disconnectAll()
  },

  startScan({ commit, rootGetters }) {
    if (!BleConnectinManager.isSupport()) {
      return
    }

    const provider = BleConnectinManager.getProvider()

    const unsubscribe = provider.addListener(
      BleProviderEventTypes.DEVICE_ADDED,
      device => {
        const bleSettings = rootGetters['appSettings/ble']
        const deviceSetting = bleSettings[device.address]

        if (deviceSetting) {
          commit('setDevice', deviceSetting)
        } else {
          commit('setDevice', { ...device, logicalConnected: false })
        }
      }
    )

    commit('setUnsubscribe', {
      eventType: BleProviderEventTypes.DEVICE_ADDED,
      unsubscribe
    })

    provider.startScan()
  },

  async stopScan({ getters, commit, dispatch }) {
    if (!BleConnectinManager.isSupport()) {
      return
    }

    await BleConnectinManager.getProvider().stopScan()

    dispatch('unsubscribe', BleProviderEventTypes.DEVICE_ADDED)
  },

  async connect(
    { commit, getters, rootGetters, dispatch },
    { address, userControl }
  ) {
    if (!BleConnectinManager.isSupport()) {
      return
    }

    await BleConnectinManager.activateConnection(address, { userControl }, true)

    commit('setConnected', { address, status: true })

    const newSetting = getters['devices'][address]
    const bleSettings = rootGetters['appSettings/ble']

    await (dispatch as any)(
      'appSettings/update',
      { ble: { ...bleSettings, [newSetting.address]: newSetting } },
      { root: true }
    )

    BleConnectinManager.setProcessing(address, false)
  },

  async disconnect({ commit, getters, rootGetters, dispatch }, { address }) {
    if (!BleConnectinManager.isSupport()) {
      return
    }

    try {
      await BleConnectinManager.disconnect(address)
    } catch (error) {
      if (['neverConnected', 'isDisconnected'].indexOf(error.error) === -1) {
        throw error
      }
      console.error('The below error is ignored.')
      console.error(error)
    }

    commit('setConnected', { address, status: false })

    const newSetting = getters['devices'][address]
    const bleSettings = rootGetters['appSettings/ble']

    delete bleSettings[newSetting.address]

    await (dispatch as any)(
      'appSettings/update',
      { ble: { ...bleSettings } },
      { root: true }
    )
  },

  unsubscribe({ commit, getters }, eventType) {
    const unsubscribe = getters['unsubscribe'][eventType]

    if (!unsubscribe) {
      console.warn(
        `unsubscribe function of eventType '${eventType}' not existent or already removed`
      )
      return
    }

    unsubscribe()

    commit('deleteUnsubscribe', eventType)
  },

  async deleteUnconnectedDevices({ commit, rootGetters, getters, dispatch }) {
    const devices = getters['devices']
    const bleSettings = rootGetters['appSettings/ble']
    const unconnectedDeviceAddresses = Object.keys(devices).filter(
      address => !devices[address].logicalConnected
    )

    if (unconnectedDeviceAddresses.some(address => bleSettings[address])) {
      unconnectedDeviceAddresses.forEach(address => {
        delete bleSettings[address]
      })
      await (dispatch as any)(
        'appSettings/update',
        { ble: { ...bleSettings } },
        { root: true }
      )
    }
    unconnectedDeviceAddresses.forEach(address => {
      commit('deleteDevice', { address })
    })
  },

  updateAutoSending: ({ commit }, sending) => {
    commit('updateAutoSending', sending)
    SynchronizedStorage.BleAutoSending.set(sending)
  },

  terminateAll: async () => {
    await BleConnectinManager.terminateAll()
  }
}

/* -------------------------------------------------------------------------- */

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
